import { Component, computed, OnDestroy, OnInit, signal, inject } from '@angular/core';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { UserSelectors } from '@next/core-lib/user';
import { select, Store } from '@ngrx/store';
import { Subject, takeUntil } from 'rxjs';

import { SiteState } from '../../../shared/models';
import { WeatherService } from '../../services/weather.service';
import { WeatherForecastDto } from '../../shared/models/weather.models';
import { SummaryCardComponent } from '@next/core-lib/components';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTooltip } from '@angular/material/tooltip';
import { DatePipe, DecimalPipe } from '@angular/common';

@Component({
  selector: 'app-weather-forecast-widget',
  templateUrl: './weather-forecast-widget.component.html',
  styleUrl: './weather-forecast-widget.component.scss',
  imports: [SummaryCardComponent, MatProgressSpinner, MatTooltip, DatePipe, DecimalPipe, TranslatePipe],
})
export class WeatherForecastWidgetComponent implements OnInit, OnDestroy {
  private readonly weatherService = inject(WeatherService);
  private readonly localeService = inject(LocaleService);
  private readonly store = inject<Store<SiteState>>(Store);

  private readonly destroy$ = new Subject<void>();
  locale = signal<string>(this.localeService.getLocale());

  weatherData = signal<WeatherForecastDto | null>(null);
  loading = signal(true);
  error = signal(false);
  locationName = signal<string | null>(null);

  subtitle = computed(() => {
    const weatherData = this.weatherData();
    const cityName =
      this.locationName() ||
      (weatherData?.location && !this.isCoordinates(weatherData.location) ? weatherData.location : null);

    if (cityName) {
      return this.localeService.translate('TGA.WeatherForecast.Subtitle', {
        cityname: cityName,
      });
    }
    return this.localeService.translate('TGA.WeatherForecast.SubtitleDefault');
  });

  // Computed signal that pre-calculates icon paths and wind SVGs for each day
  weatherDays = computed(() => {
    const data = this.weatherData();
    if (!data?.days) return [];

    return data.days.map((day) => ({
      ...day,
      iconPath: this.getWeatherIconPath(day.conditions),
      windSvg: this.getWindDirectionSvg(day.windName, day.windSpeedBeaufort),
    }));
  });

  constructor() {
    this.localeService.localeDataChanged$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.locale.set(this.localeService.getLocale());
    });

    // Get location name from ship-to
    this.store.pipe(select(UserSelectors.selectLocation), takeUntil(this.destroy$)).subscribe((location) => {
      // Build full address: Street Number, City
      const addressParts: string[] = [];

      if (location?.street) {
        let street = location.street;
        if (location.houseNumber) {
          street += ` ${location.houseNumber}`;
        }
        addressParts.push(street);
      }

      if (location?.city) {
        addressParts.push(location.city);
      }

      this.locationName.set(addressParts.length > 0 ? addressParts.join(', ') : location?.name || null);
    });
  }

  ngOnInit(): void {
    this.loadWeatherData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadWeatherData(): void {
    this.loading.set(true);
    this.error.set(false);

    this.weatherService
      .getWeatherForecast()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.weatherData.set(data);
          this.loading.set(false);
        },
        error: () => {
          this.error.set(true);
          this.loading.set(false);
        },
      });
  }

  private getWeatherIconPath(conditions: string): string {
    // Map weather conditions to icon filenames
    // Support both English codes and Dutch/localized condition names
    const conditionsLower = conditions?.toLowerCase() || '';

    const iconMap: { [key: string]: string } = {
      // English condition codes
      'clear-day': 'clear-day.svg',
      'clear-night': 'clear-night.svg',
      cloudy: 'cloudy.svg',
      fog: 'fog.svg',
      'partly-cloudy-day': 'partly-cloudy-day.svg',
      'partly-cloudy-night': 'partly-cloudy-night.svg',
      rain: 'rain.svg',
      snow: 'snow.svg',
      wind: 'wind.svg',
      hail: 'hail.svg',
      sleet: 'sleet.svg',
      thunder: 'thunder.svg',
      'thunder-rain': 'thunder-rain.svg',
      'rain, cloudy': 'rain.svg',
      'rain, overcast': 'rain.svg',
      'Rain, Partially cloudy': 'showers-day.svg',
      sunny: 'clear-day.svg',
      clear: 'clear-day.svg',
      'partially cloudy': 'partly-cloudy-day.svg',
      'partly cloudy': 'partly-cloudy-day.svg',
      'lightly cloudy': 'partly-cloudy-day.svg',
      thunderstorms: 'thunder-rain.svg',
      showers: 'showers-day.svg',
      'rain showers': 'showers-day.svg',

      // Dutch condition names
      regen: 'rain.svg',
      'regen, bewolkt': 'rain.svg',
      'regen, licht bewolkt': 'showers-day.svg',
      bewolkt: 'cloudy.svg',
      zonnig: 'clear-day.svg',
      helder: 'clear-day.svg',
      mist: 'fog.svg',
      'gedeeltelijk bewolkt': 'partly-cloudy-day.svg',
      halfbewolkt: 'partly-cloudy-day.svg',
      'licht bewolkt': 'partly-cloudy-day.svg',
      sneeuw: 'snow.svg',
      hagel: 'hail.svg',
      ijzel: 'sleet.svg',
      onweer: 'thunder.svg',
      onweersbuien: 'thunder-rain.svg',
      buien: 'showers-day.svg',
      regenbuien: 'showers-day.svg',
    };

    const iconFileName = iconMap[conditionsLower] || iconMap[conditions] || 'cloudy.svg';
    return `assets/icons/weather/${iconFileName}`;
  }

  private getWindDirectionSvg(windDirectionCardinal: string, windSpeedBeaufort: number): string {
    // Map cardinal directions to rotation angles (0° = North pointing up)
    const rotationMap: { [key: string]: number } = {
      N: 0,
      NO: 45,
      NE: 45,
      O: 90,
      E: 90,
      ZO: 135,
      SE: 135,
      Z: 180,
      S: 180,
      ZW: 225,
      SW: 225,
      W: 270,
      NW: 315,
    };

    const rotation = rotationMap[windDirectionCardinal?.toUpperCase()] || 0;
    const speed = Math.round(windSpeedBeaufort || 0);

    // Generate inline SVG with rotation and wind speed
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="80" height="80">
        <g transform="rotate(${rotation}, 50, 50)">
          <path d="M50 10 L65 70 L50 65 L35 70 Z" fill="#979696"/>
        </g>
        <circle cx="50" cy="50" r="14" fill="white" stroke="#b4b4b4" stroke-width="2"/>
        <text x="50" y="50" text-anchor="middle" dominant-baseline="middle"
              font-family="system-ui, sans-serif" font-size="14" font-weight="700" fill="#000">
          ${speed}
        </text>
      </svg>
    `.trim();

    // eslint-disable-next-line no-undef
    return 'data:image/svg+xml;base64,' + btoa(svg);
  }

  capitalizeDayName(dayName: string): string {
    if (!dayName) return '';
    return dayName.charAt(0).toUpperCase() + dayName.slice(1).toLowerCase();
  }

  private isCoordinates(location: string): boolean {
    return /^-?\d+\.?\d*,\s*-?\d+\.?\d*$/.test(location);
  }
}
